import { useEffect } from 'react';
import { X, FileText, Shield, CheckCircle } from 'lucide-react';
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import { useLanguage } from '../contexts/LanguageContext';
import { termsContent } from '../constants/termsContent';
import { TermsSectionComponent } from './terms/TermsSection';

interface TermsAndConditionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function TermsAndConditionsModal({ isOpen, onClose }: TermsAndConditionsModalProps) {
  const { t } = useLanguage();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />
      
      <div className="relative z-10 w-full max-w-4xl max-h-[90vh] transition-all duration-300 transform scale-100">
        <Card className="border-[#f91bdc]/20 shadow-2xl overflow-hidden">
          <CardHeader className="relative bg-gradient-to-r from-[#f91bdc]/5 to-[#f91bdc]/10 border-b border-[#f91bdc]/20">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onClose}
              className="absolute right-4 top-4 h-8 w-8 p-0 hover:bg-white/50 z-10"
            >
              <X size={18} />
            </Button>
            
            <div className="flex items-center space-x-3 pr-12">
              <div className="w-12 h-12 bg-[#f91bdc] text-white rounded-lg flex items-center justify-center">
                <FileText size={24} />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold text-gray-900">
                  {t('terms.title')}
                </CardTitle>
                <p className="text-gray-600 mt-1">
                  {t('terms.subtitle')}
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2 mt-4">
              <Badge variant="outline" className="border-[#f91bdc] text-[#f91bdc]">
                <Shield size={12} className="mr-1" />
                {t('terms.shariahCompliant')}
              </Badge>
              <Badge variant="outline" className="border-[#f91bdc] text-[#f91bdc]">
                {t('terms.lastUpdated').replace('{year}', new Date().getFullYear().toString())}
              </Badge>
              <Badge variant="outline" className="border-[#f91bdc] text-[#f91bdc]">
                <CheckCircle size={12} className="mr-1" />
                {t('terms.scholarReviewed')}
              </Badge>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            <ScrollArea className="h-[60vh] p-6">
              <div className="space-y-8">
                {/* Introduction */}
                <section>
                  <div className="bg-[#f91bdc]/5 rounded-lg p-6 mb-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('terms.welcome')}</h3>
                    <p className="text-gray-700 leading-relaxed">
                      {t('terms.welcomeText')}
                    </p>
                  </div>
                </section>

                {/* Terms Sections */}
                {termsContent.map((section, index) => (
                  <TermsSectionComponent key={index} section={section} index={index} />
                ))}

                {/* Important Notice */}
                <section className="bg-[#f91bdc]/5 rounded-lg p-6 border border-[#f91bdc]/20">
                  <div className="flex items-start space-x-3">
                    <Shield className="text-[#f91bdc] mt-1" size={20} />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('terms.importantNotice')}</h4>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        {t('terms.importantNoticeText')}
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            </ScrollArea>
          </CardContent>

          <div className="border-t border-gray-200 p-4 bg-gray-50">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">
                {t('terms.lastUpdatedDate').replace('{date}', new Date().toLocaleDateString())}
              </p>
              <Button 
                onClick={onClose}
                className="bg-[#f91bdc] hover:bg-[#e015c9] text-white px-6"
              >
                {t('terms.iUnderstand')}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}